import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const UserCarsViewModal = ({
  isOpen,
  onClose,
  item,
  language,
  onEdit,
  onDelete,
}) => {
  if (!isOpen || !item) return null;

  const labels = {
    title: { en: "Car Profile Details", ar: "تفاصيل ملف السيارة" },

    // basic
    user: { en: "User", ar: "المستخدم" },
    carType: { en: "Car Type", ar: "نوع السيارة" },
    model: { en: "Model", ar: "الطراز" },
    year: { en: "Year", ar: "السنة" },
    licensePlate: { en: "License Plate", ar: "لوحة الترخيص" },
    color: { en: "Color", ar: "اللون" },
    notes: { en: "Notes", ar: "ملاحظات" },
    vehicleBrand: { en: "Brand", ar: "الماركة" },

    // oil
    oilSection: { en: "Oil Info", ar: "معلومات الزيت" },
    oilName: { en: "Oil Name", ar: "اسم الزيت" },
    oilRank: { en: "Oil Rank", ar: "درجة الزيت" },
    oilMeter: { en: "Oil Meter", ar: "عداد الزيت" },
    oilChangedAt: { en: "Oil Changed At", ar: "تاريخ تغيير الزيت" },
    oilCenter: { en: "Oil Change Center", ar: "مركز تغيير الزيت" },

    // tire
    tireSection: { en: "Tires", ar: "الإطارات" },
    tireName: { en: "Tire Name", ar: "اسم الإطار" },
    tireType: { en: "Tire Type", ar: "نوع الإطار" },
    tireChangedAt: { en: "Tire Changed At", ar: "تاريخ تغيير الإطار" },

    // battery
    batterySection: { en: "Battery", ar: "البطارية" },
    batteryType: { en: "Battery Type", ar: "نوع البطارية" },
    batteryChangedAt: { en: "Battery Changed At", ar: "تاريخ تغيير البطارية" },
    batteryCenter: { en: "Battery Center", ar: "مركز البطارية" },

    // filters
    filterSection: { en: "Filters", ar: "الفلاتر" },
    airFilter: { en: "Air Filter", ar: "فلتر الهواء" },
    oilFilter: { en: "Oil Filter", ar: "فلتر الزيت" },
    coolingFilter: { en: "Cooling Filter", ar: "فلتر التبريد" },
    petrolFilter: { en: "Petrol Filter", ar: "فلتر البنزين" },

    damages: { en: "Damage Notes", ar: "ملاحظات الأضرار" },

    edit: { en: "Edit", ar: "تعديل" },
    delete: { en: "Delete", ar: "حذف" },
  };

  const Field = ({ label, value }) => (
    <div>
      <label className="text-sm font-medium text-text-secondary">
        {label}
      </label>
      <p className="text-base font-medium text-foreground mt-1">
        {value ?? "-"}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-foreground">
            {labels.title[language]}
          </h2>

          <button onClick={onClose}>
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* BASIC INFO */}
          <div className="grid grid-cols-2 gap-4">
            <Field label={labels.user[language]} value={`${item.owner?.first_name} ${item.owner?.last_name}`} />
            <Field label={labels.vehicleBrand[language]} value={item.vehicle_brand?.name} />
            <Field label={labels.carType[language]} value={item.vehicle_type} />
            <Field label={labels.model[language]} value={item.vehicle_model} />
            <Field label={labels.year[language]} value={item.vehicle_year} />
            <Field label={labels.licensePlate[language]} value={item.vehicle_number} />
            <Field label={labels.color[language]} value={item.vehicle_color} />
            <Field label={labels.notes[language]} value={item.notes} />
          </div>

          {/* OIL */}
          <div className="border-t pt-4 space-y-2">
            <h3 className="font-semibold">{labels.oilSection[language]}</h3>

            <div className="grid grid-cols-2 gap-4">
              <Field label={labels.oilName[language]} value={item.oil_name} />
              <Field label={labels.oilRank[language]} value={item.oil_rank} />
              <Field label={labels.oilMeter[language]} value={item.oil_meter_value} />
              <Field label={labels.oilChangedAt[language]} value={item.oil_changed_at} />
              <Field label={labels.oilCenter[language]} value={item.oil_change_center} />
            </div>
          </div>

          {/* TIRES */}
          <div className="border-t pt-4 space-y-2">
            <h3 className="font-semibold">{labels.tireSection[language]}</h3>

            <div className="grid grid-cols-2 gap-4">
              <Field label={labels.tireName[language]} value={item.tire_name} />
              <Field label={labels.tireType[language]} value={item.tire_type} />
              <Field label={labels.tireChangedAt[language]} value={item.tire_changed_at} />
            </div>
          </div>

          {/* BATTERY */}
          <div className="border-t pt-4 space-y-2">
            <h3 className="font-semibold">{labels.batterySection[language]}</h3>

            <div className="grid grid-cols-2 gap-4">
              <Field label={labels.batteryType[language]} value={item.battery_type} />
              <Field label={labels.batteryChangedAt[language]} value={item.battery_changed_at} />
              <Field label={labels.batteryCenter[language]} value={item.battery_change_center} />
            </div>
          </div>

          {/* FILTERS */}
          <div className="border-t pt-4 space-y-2">
            <h3 className="font-semibold">{labels.filterSection[language]}</h3>

            <div className="grid grid-cols-2 gap-4">
              <Field label={labels.airFilter[language]} value={item.air_filter_changed_at} />
              <Field label={labels.oilFilter[language]} value={item.oil_filter_changed_at} />
              <Field label={labels.coolingFilter[language]} value={item.cooling_filter_changed_at} />
              <Field label={labels.petrolFilter[language]} value={item.petrol_filter_changed_at} />
            </div>
          </div>

          {/* DAMAGES */}
          <div className="border-t pt-4">
            <Field label={labels.damages[language]} value={item.damages_note} />
          </div>

          {/* ACTIONS */}
          <div className="flex gap-3 pt-4">
            <Button variant="destructive" fullWidth onClick={onDelete}>
              {labels.delete[language]}
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserCarsViewModal;